/**
 * useTutorLiveFeedActions — les gestes des cartes du fil de la séance, au protocole de la prod.
 *
 * Port de `common/liveMode/liveV3/tutorEventsPanel/useTutorEventsActions.js` : chaque geste fait les mêmes
 * appels d'API, écrit les mêmes tranches Redux et envoie le même message de salle, avec les mêmes champs
 * (tech-derisk, point 7). La table `TUTOR_LIVE_FEED_EQUIVALENCE` les nomme, un test par ligne la tient
 * (`useTutorLiveFeedActions.equivalence.test.tsx`). Ce qui change : la salle part par le moteur commun
 * (`live.send`), l'élève épinglé par la sélection partagée (`selection.select`, l'ancien `onPinStudent`),
 * l'activité ouverte par `selection.openActivityFor`, et le geste laisse sa trace dans la mémoire du fil
 * (`decideTutorLiveSession`) au lieu des 3 s de la carte verte.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import {
    clearRecentFinalizedActivity,
    markConversationAsRead,
    pinStudentInDialogueAtomic,
    requestStudentActivityFocus,
    setCurrentStudentActivity,
    setOngoingCheckpointIds,
} from "../../../../../../data/redux/actions/liveMode"
import { dismissTutorEvent, markTutorEventSamSent } from "../../../../../../data/redux/reducers/data/tutorEventsPanel"
import { decideTutorLiveSession } from "../../../../../../data/redux/reducers/data/tutorLiveSession"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { type UITutorLiveSnackbar } from "../../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { checkpointContextOf, samSentKey } from "../../../../view-models/tutor/Live/tutorLiveFeedRules"
import { type TutorLiveCard } from "../../../../view-models/tutor/Live/tutorLiveFeedViewModel"
import { rawId, type TutorLiveSelection } from "./tutorLiveContract"

/**
 * Geste → appel → message, la parité au protocole de la prod (registre F-76 à F-83, F-110, F-48, C-23, G-14,
 * G-17). Chaque ligne a son test.
 *
 * `drop` : un message de salle qui ne part pas hors connexion est perdu, comme avec l'ancien
 * `useLiveKitEventSender`, qui n'en gardait aucun ; le moteur ne le rejoue pas à la reconnexion.
 */
export const TUTOR_LIVE_FEED_EQUIVALENCE = [
    {
        gesture: "Parler à {prénom} (talk)",
        legacy: "useTutorEventsActions.pinAndSetDialogue",
        service:
            "pinStudentInDialogueAtomic : liveServiceV2.setSoundModeV2 {id} {enabled: false, user_ids: autres en Dialogue}, puis {enabled: true, user_ids: [élève]}, puis lowerHandsV2 {id} {user_ids: [élève]}",
        message: null,
    },
    {
        gesture: "Envoyer le champion à {prénom} (send_sam)",
        legacy: "useTutorEventsActions.sendSam",
        service: "setOngoingCheckpointIds([élève], add), markTutorEventSamSent(`${type}-${élève}`)",
        message:
            "checkpointTriggerResponse {studentId, accepted: true, tutorId, trigger_context: raise_hand | silent_mode_10min, trigger_source: tutor_events_panel, timestamp} → [élève], drop",
    },
    {
        gesture: "Notifier {prénom} (notify, natif)",
        legacy: "useTutorEventsActions.notifyStudent",
        service: "liveServiceV2.makeCallV2 {id} {user_ids: [élève]}, sans la boîte réseau (apiCall)",
        message: null,
    },
    {
        gesture: "Voir le rendu de {prénom} (open_work)",
        legacy: "useTutorEventsActions.openStudentActivity",
        service:
            "setCurrentStudentActivity(élève, activité), requestStudentActivityFocus(élève, activité), clearRecentFinalizedActivity(élève)",
        message: null,
    },
    {
        gesture: "La croix (dismiss)",
        legacy: "useTutorEventsActions.dismissEvent",
        service: "dismissTutorEvent(empreinte), clearRecentFinalizedActivity(élève)",
        message: null,
    },
    {
        gesture: "Lire le résumé, ou l'écarter (sam_summary)",
        legacy: "LiveParticipant.handleCloseConversation",
        service: "markConversationAsRead(séance, élève, conversation)",
        message: null,
    },
    {
        gesture: "Utile / Pas utile (le résumé)",
        legacy: "AgentSummaryModal.handleFeedback",
        service:
            "liveService.giveAgentFeedback {id: conversation} {feedback_type: tutor, feedback_value: ±1}, puis liveServiceV2.getLiveSessionV2 {id}, sans la boîte réseau (apiCall)",
        message: null,
    },
    {
        gesture: "Ajouter à sa liste (une activité générée, un élève)",
        legacy: "GenerateActivityModal.createActivity",
        service:
            "activitiesService.createActivityAsTutor {id: élève} {material_id | sub_material_id, has_prerequisite_quiz}",
        message: "newTodo {id: élève, activity} → [élève], drop",
    },
    {
        gesture: "Ajouter à leur liste (tout le groupe)",
        legacy: "GenerateActivityModal.createActivity (forStudents)",
        service:
            "activitiesService.activityTodoForStudents {} {material_id | sub_material_id, has_prerequisite_quiz, student_ids, live_session_id}",
        message: "newTodo {id: activity.student_id, activity} → [activity.student_id], une par activité créée, drop",
    },
    {
        gesture: "Ajouter à leur liste (tout le groupe, un seul inscrit)",
        legacy: "GenerateActivityModal.createActivity (forStudents && selectedStudentIds.length > 0)",
        service:
            "activitiesService.activityTodoForStudents {} {material_id | sub_material_id, has_prerequisite_quiz, student_ids: [élève], live_session_id}, jamais createActivityAsTutor",
        message: "newTodo {id: activity.student_id, activity} → [activity.student_id], drop",
    },
    {
        gesture: "Montrer à l'élève (une activité générée)",
        legacy: "TodoModal « Afficher à l'élève » après la création",
        service: "la création ci-dessus",
        message: "newTodo {id: élève, activity, forceFocus: true} → [élève], drop",
    },
] as const

export type TutorLiveFeedActionsDeps = {
    liveSessionId: number | null
    /** Le tuteur (`tutorId` du message). */
    meId: string
    send:
        ((type: string, data: Record<string, unknown>, to: number[] | null, policy: "keep" | "drop") => unknown) | null
    selection: Pick<TutorLiveSelection, "select" | "openActivityFor">
    /** Les élèves inscrits, présents, en Dialogue (en chaînes). */
    studentIds: string[]
    present: string[]
    soundModeIds: string[]
    firstnameOf: (studentId: string) => string
    isWeb: boolean
    /** La clé de la mémoire du fil (`compte·séance`). */
    memoryKey: string | null
    /** Le résumé du champion d'un élève : la feuille (`samSummary`). */
    openSummary: (studentId: string) => void
}

export type TutorLiveFeedActions = {
    run: (card: TutorLiveCard, actionId: string) => void
    dismiss: (card: TutorLiveCard) => void
    snackbar: { value: UITutorLiveSnackbar; onDismiss: () => void } | null
    /**
     * La carte en vol (F-62) : un geste parti dont la réponse est attendue (la bascule en Dialogue), la forme
     * de `TutorSessionFeed.pending` (sa roue sur le bouton de la carte).
     */
    pending: { itemId: string; actionId: string } | null
    /** Un accusé, d'un autre geste du fil (la génération). */
    notify: (message: string, tone: UITutorLiveSnackbar["tone"], durationMs?: number) => void
}

type AtomicResult = { ok?: boolean; inFlight?: boolean; message?: string } | undefined

export function useTutorLiveFeedActions(deps: TutorLiveFeedActionsDeps): TutorLiveFeedActions {
    const dispatch = useDispatch()
    const [snack, setSnack] = useState<UITutorLiveSnackbar | null>(null)
    const [inFlight, setInFlight] = useState<{ itemId: string; actionId: string } | null>(null)
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const depsRef = useRef(deps)
    depsRef.current = deps

    const notify = useCallback((message: string, tone: UITutorLiveSnackbar["tone"], durationMs = 3000) => {
        if (timer.current != null) clearTimeout(timer.current)
        setSnack({ message, tone })
        timer.current = setTimeout(() => setSnack(null), durationMs)
    }, [])
    useEffect(
        () => () => {
            if (timer.current != null) clearTimeout(timer.current)
        },
        []
    )

    const decide = useCallback(
        (card: TutorLiveCard, text: string) => {
            dispatch(decideTutorLiveSession(depsRef.current.memoryKey, card.id, text) as never)
        },
        [dispatch]
    )

    const offline = useCallback(
        (studentId: string) => {
            const d = depsRef.current
            if (d.present.includes(studentId)) return false
            notify(`${d.firstnameOf(studentId)} est hors-ligne`, "danger", 3500)
            return true
        },
        [notify]
    )

    /** `pinAndSetDialogue` : la bascule atomique, puis l'élève épinglé ; vrai sur un succès. */
    const talk = useCallback(
        async (studentId: string): Promise<boolean> => {
            const d = depsRef.current
            if (offline(studentId)) return false
            const others = d.soundModeIds.filter((id) => d.studentIds.includes(id) && id !== studentId).map(rawId)
            const result = (await (dispatch(
                pinStudentInDialogueAtomic({
                    liveSessionId: d.liveSessionId,
                    studentId: rawId(studentId),
                    otherStudentsInDialogue: others,
                }) as never
            ) as unknown as Promise<AtomicResult>)) as AtomicResult
            if (!result?.ok) {
                // Un clic de plus pendant le vol se tait : rien ne s'est mal passé.
                if (result?.inFlight) return false
                notify(
                    result?.message ?? `Impossible de basculer ${d.firstnameOf(studentId)} en Dialogue`,
                    "danger",
                    3000
                )
                return false
            }
            d.selection.select(studentId)
            return true
        },
        [dispatch, notify, offline]
    )

    const sendHelper = useCallback(
        (card: TutorLiveCard) => {
            const d = depsRef.current
            if (card.signal === "sam_summary") return
            const context = checkpointContextOf(card.signal)
            if (context == null) return
            dispatch(setOngoingCheckpointIds([rawId(card.studentId)], "add") as never)
            dispatch(markTutorEventSamSent(samSentKey(card.signal, card.studentId)) as never)
            d.send?.(
                "checkpointTriggerResponse",
                {
                    studentId: rawId(card.studentId),
                    accepted: true,
                    tutorId: rawId(d.meId),
                    trigger_context: context,
                    trigger_source: "tutor_events_panel",
                    timestamp: Date.now(),
                },
                [Number(card.studentId)],
                "drop"
            )
        },
        [dispatch]
    )

    const openActivity = useCallback(
        (card: TutorLiveCard) => {
            const d = depsRef.current
            if (offline(card.studentId)) return false
            const id = rawId(card.studentId)
            if (card.activityId != null) {
                dispatch(setCurrentStudentActivity(id, rawId(card.activityId)) as never)
                dispatch(requestStudentActivityFocus(id, rawId(card.activityId)) as never)
                d.selection.openActivityFor(card.studentId, card.activityId)
            } else d.selection.select(card.studentId)
            dispatch(clearRecentFinalizedActivity(id) as never)
            return true
        },
        [dispatch, offline]
    )

    const readSummary = useCallback(
        (card: TutorLiveCard) => {
            const d = depsRef.current
            if (card.conversationId != null) {
                dispatch(
                    markConversationAsRead(d.liveSessionId, rawId(card.studentId), rawId(card.conversationId)) as never
                )
                decide(card, `Résumé de ${d.firstnameOf(card.studentId)} lu`)
            }
            d.openSummary(card.studentId)
        },
        [decide, dispatch]
    )

    /** La bascule en Dialogue d'une carte, sa roue tenue le temps de la réponse. */
    const talkFrom = useCallback(
        (card: TutorLiveCard, actionId: string) => {
            const firstname = depsRef.current.firstnameOf(card.studentId)
            setInFlight({ itemId: card.id, actionId })
            void talk(card.studentId)
                .then((ok) => ok && decide(card, `Tu parles à ${firstname}`))
                .finally(() => setInFlight((current) => (current?.itemId === card.id ? null : current)))
        },
        [decide, talk]
    )

    const run = useCallback(
        (card: TutorLiveCard, actionId: string) => {
            const d = depsRef.current
            const firstname = d.firstnameOf(card.studentId)
            switch (actionId) {
                case "talk":
                    talkFrom(card, actionId)
                    return
                case "send_sam":
                    sendHelper(card)
                    return
                case "notify":
                    if (d.isWeb) {
                        talkFrom(card, actionId)
                        return
                    }
                    // Sans la boîte réseau legacy, la tranche remise au repos (`apiCall`) ; l'accusé part tout de
                    // suite, comme l'ancien `notifyStudent`, qui n'attendait pas la réponse.
                    void apiCall(dispatch as never, liveServiceV2.makeCallV2, {
                        routeParams: { id: d.liveSessionId },
                        data: { user_ids: [rawId(card.studentId)] },
                    }).catch(() => undefined)
                    notify(`📞 Notification envoyée à ${firstname}`, "success", 2500)
                    decide(card, `${firstname} notifié`)
                    return
                case "open_work":
                    if (openActivity(card)) decide(card, `Rendu de ${firstname} ouvert`)
                    return
                case "read_summary":
                    readSummary(card)
                    return
                default:
                    return
            }
        },
        [decide, dispatch, notify, openActivity, readSummary, sendHelper, talkFrom]
    )

    const dismiss = useCallback(
        (card: TutorLiveCard) => {
            const d = depsRef.current
            if (card.signal === "sam_summary") {
                if (card.conversationId != null)
                    dispatch(
                        markConversationAsRead(
                            d.liveSessionId,
                            rawId(card.studentId),
                            rawId(card.conversationId)
                        ) as never
                    )
                decide(card, "Écarté")
                return
            }
            dispatch(dismissTutorEvent(card.id) as never)
            if (card.signal === "exercise_done") dispatch(clearRecentFinalizedActivity(rawId(card.studentId)) as never)
            decide(card, "Écarté")
        },
        [decide, dispatch]
    )

    return {
        run,
        dismiss,
        snackbar: snack != null ? { value: snack, onDismiss: () => setSnack(null) } : null,
        pending: inFlight,
        notify,
    }
}
